import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { requestPasswordReset } from '@/lib/api/auth'
import { Mail, ArrowLeft, Check } from 'lucide-react'

const forgotPasswordSchema = z.object({
  email: z.string().email('Adresse email invalide'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)

    try {
      await requestPasswordReset(data.email)
    } catch (error) {
      // Always show success (security best practice - don't reveal if email exists)
      console.error('Password reset request failed:', error)
    } finally {
      // Always show success page regardless of backend response
      setSubmittedEmail(data.email)
      setSubmitted(true)
      setIsLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-12">
            <h1 className="text-6xl font-bold text-primary mb-4">SOLY</h1>
            <div className="w-32 h-1.5 bg-primary mx-auto rounded-full mb-6"></div>
          </div>

          {/* Success card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {/* Success icon */}
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>

            {/* Success message */}
            <h2 className="text-2xl font-bold text-center mb-4">
              Vérifiez votre email
            </h2>

            <p className="text-center text-muted-foreground mb-6">
              Si un compte existe avec l'adresse{' '}
              <span className="font-medium text-foreground">{submittedEmail}</span>,
              nous avons envoyé les instructions pour réinitialiser votre mot de passe.
            </p>

            <div className="bg-muted border border-border rounded-lg p-4 mb-6">
              <p className="text-sm text-foreground">
                <strong>💡 Conseil :</strong> Le lien expire dans 30 minutes.
                Si vous ne recevez pas l'email, vérifiez vos spams.
              </p>
            </div>

            {/* Back to login */}
            <Link to="/login">
              <Button variant="outline" className="w-full h-12">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la connexion
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold text-primary mb-4">SOLY</h1>
          <div className="w-32 h-1.5 bg-primary mx-auto rounded-full mb-6"></div>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-center mb-2">
            Mot de passe oublié ?
          </h2>
          <p className="text-center text-muted-foreground mb-8">
            Entrez votre email pour recevoir un lien de réinitialisation
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email input */}
            <div className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Votre adresse email"
                  autoComplete="email"
                  {...register('email')}
                  autoFocus
                  data-testid="forgot-email-input"
                  className={`pl-12 h-14 text-base ${
                    errors.email ? 'border-red-500 animate-shake' : ''
                  }`}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600 flex items-center gap-1 animate-shake">
                  <span>⚠️</span>
                  <span>{errors.email.message}</span>
                </p>
              )}
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              data-testid="forgot-submit-btn"
              className="w-full h-12 text-base font-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  Envoi en cours...
                </>
              ) : (
                'Envoyer le lien de réinitialisation'
              )}
            </Button>
          </form>

          {/* Back to login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary hover:underline font-medium inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
